// src/services/aiService.js
// Minimal client for backend AI scoring/optimization route.
// Usage:
//   import { getScoresAndOptimizations } from './aiService';
//   const data = await getScoresAndOptimizations({ profileUrl, scrapedData, questionnaire, options });

import consoleLogger from '../utils/consoleLogger';

const BASE_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';

// Retry / timeout configuration for the Claude Sonnet 4.6 audit pipeline.
//
// DEFAULT_RETRIES is intentionally LOW (1) because each retry is a fresh
// paid Claude call — a 504 that fired once is highly likely to fire again
// at the same input size, and retries burn ~$0.10 each.
//
// REQUEST_TIMEOUT_MS is raised to 240s because Claude audits routinely run
// 115–180s (per sibling calibration). Nginx proxy_read_timeout on the
// server MUST also be 240s+ for this to be effective.
export const AI_SERVICE_CONFIG = {
  DEFAULT_RETRIES: 1,
  MAX_BACKOFF_MS: 8000,
  STORED_ANALYSIS_RETRIES: 2,
  REQUEST_TIMEOUT_MS: 240000
};

// Module-level dedup: if a request for the same profileUrl is already
// in flight, return the live promise instead of firing a second paid
// Claude call. Cleared when the promise settles.
const inFlight = new Map();

/**
 * Helper function to implement exponential backoff retry
 */
function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Calls backend AI endpoint to get scores and optimizations with retry logic.
 * @param {Object} params
 * @param {string} params.profileUrl
 * @param {Object} params.scrapedData
 * @param {Object} params.questionnaire
 * @param {Object} [params.options] - optional { signal, retries } for aborting and retry control
 * @returns {Promise<Object>} Strict JSON as defined by the backend
 */
export async function getScoresAndOptimizations(params) {
  const key = params?.profileUrl || '';
  // Dedup concurrent calls for the same profile — a double-click on the
  // Analyze button would otherwise trigger two paid Claude audits.
  if (key && inFlight.has(key)) {
    console.log('[AI Service] Returning in-flight request for', key);
    return inFlight.get(key);
  }
  const promise = _getScoresAndOptimizationsImpl(params);
  if (key) {
    inFlight.set(key, promise);
    promise.finally(() => inFlight.delete(key));
  }
  return promise;
}

async function _getScoresAndOptimizationsImpl({ profileUrl, scrapedData, questionnaire, options = {} }) {
  const { signal, retries = AI_SERVICE_CONFIG.DEFAULT_RETRIES } = options;
  const maxRetries = Math.max(0, retries);
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Create timeout controller
      const timeoutController = new AbortController();
      const timeoutId = setTimeout(() => timeoutController.abort(), AI_SERVICE_CONFIG.REQUEST_TIMEOUT_MS);
      
      // Combine user signal with timeout signal
      const combinedSignal = signal || timeoutController.signal;
      
      // Get console logs from consoleLogger
      let consoleLogs = '';
      try {
        consoleLogs = consoleLogger.getLogs();
        console.log('[AI Service] Captured', consoleLogs.split('\n').length, 'log lines');
      } catch (e) {
        console.warn('[AI Service] Could not capture console logs:', e.message);
      }
      
      const res = await fetch(`${BASE_URL}/api/ai/score-optimize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          profileUrl, 
          scrapedData, 
          questionnaire,
          summary: consoleLogs // Include console logs
        }),
        signal: combinedSignal
      });
      
      clearTimeout(timeoutId);

      const text = await res.text();
      let data;
      
      try {
        data = text ? JSON.parse(text) : null;
      } catch (e) {
        console.warn(`[AI Service] JSON parse error on attempt ${attempt + 1}:`, e.message);
        throw new Error(`AI response is not valid JSON: ${text?.slice(0, 300)}`);
      }

      // Handle different response scenarios
      if (!res.ok) {
        const errorMsg = (data && data.error) || res.statusText || 'Server error';
        const errorDetails = (data && data.details) || '';
        const errorHint = (data && data.hint) || '';
        
        // 504 Gateway Timeout - retry with longer backoff
        if (res.status === 504) {
          if (attempt < maxRetries) {
            const backoffMs = Math.min(2000 * Math.pow(2, attempt), AI_SERVICE_CONFIG.MAX_BACKOFF_MS);
            console.warn(`[AI Service] Timeout on attempt ${attempt + 1}/${maxRetries + 1}, retrying in ${backoffMs}ms`);
            await delay(backoffMs);
            continue;
          }
          throw new Error(`Request timeout after ${maxRetries + 1} attempts. ${errorHint || 'The analysis is taking longer than expected.'}`);
        }
        
        // 502 Bad Gateway - transient server errors, retry
        if (res.status === 502) {
          if (attempt < maxRetries) {
            const backoffMs = Math.min(1000 * Math.pow(2, attempt), AI_SERVICE_CONFIG.MAX_BACKOFF_MS);
            console.warn(`[AI Service] Server error on attempt ${attempt + 1}/${maxRetries + 1}, retrying in ${backoffMs}ms: ${errorMsg}`);
            await delay(backoffMs);
            continue;
          }
          throw new Error(`AI analysis failed after ${maxRetries + 1} attempts: ${errorMsg}. ${errorHint}`);
        }

        // 503 Service Unavailable - memory/resource limits, don't retry
        if (res.status === 503) {
          console.error(`[AI Service] Service unavailable: ${errorMsg}`);
          throw new Error(`Service temporarily unavailable: ${errorMsg}. ${errorHint}`);
        }
        
        // 500 Internal Server Error - likely OpenAI API issue, retry
        if (res.status === 500) {
          if (attempt < maxRetries) {
            const backoffMs = Math.min(1500 * Math.pow(2, attempt), AI_SERVICE_CONFIG.MAX_BACKOFF_MS);
            console.warn(`[AI Service] Internal error on attempt ${attempt + 1}/${maxRetries + 1}, retrying in ${backoffMs}ms: ${errorMsg}`);
            await delay(backoffMs);
            continue;
          }
          throw new Error(`Server error after ${maxRetries + 1} attempts: ${errorMsg}. ${errorHint}`);
        }
        
        // 429 Rate Limit - retry with exponential backoff
        if (res.status === 429) {
          if (attempt < maxRetries) {
            const backoffMs = Math.min(3000 * Math.pow(2, attempt), AI_SERVICE_CONFIG.MAX_BACKOFF_MS);
            console.warn(`[AI Service] Rate limited on attempt ${attempt + 1}/${maxRetries + 1}, retrying in ${backoffMs}ms`);
            await delay(backoffMs);
            continue;
          }
          throw new Error(`Rate limit exceeded after ${maxRetries + 1} attempts. Please try again later.`);
        }
        
        // 4xx errors - check if retryable
        if (res.status >= 400 && res.status < 500) {
          // If backend explicitly says "don't retry", respect it (saves money)
          const retryable = data && data.retryable !== false;
          
          if (!retryable) {
            console.error(`[AI Service] Non-retryable error ${res.status}: ${errorMsg}`);
            throw new Error(`${errorMsg}${errorDetails ? ': ' + errorDetails : ''}`);
          }
          
          // Otherwise retry once for client errors (might be transient)
          if (attempt < 1) {
            const backoffMs = 2000;
            console.warn(`[AI Service] Client error ${res.status} on attempt ${attempt + 1}, retrying once: ${errorMsg}`);
            await delay(backoffMs);
            continue;
          }
          throw new Error(`${errorMsg}${errorDetails ? ': ' + errorDetails : ''}`);
        }
        
        // Other errors - generic retry
        if (attempt < maxRetries) {
          const backoffMs = Math.min(1000 * Math.pow(2, attempt), AI_SERVICE_CONFIG.MAX_BACKOFF_MS);
          console.warn(`[AI Service] Error ${res.status} on attempt ${attempt + 1}/${maxRetries + 1}, retrying in ${backoffMs}ms`);
          await delay(backoffMs);
          continue;
        }
        
        throw new Error(`${errorMsg}${errorHint ? ' ' + errorHint : ''}`);
      }

      // Success case
      return data;
      
    } catch (error) {
      // Handle network errors, timeouts, etc.
      if (error.name === 'AbortError') {
        throw error; // Don't retry aborted requests
      }
      
      if (attempt < maxRetries) {
        const backoffMs = Math.min(1000 * Math.pow(2, attempt), AI_SERVICE_CONFIG.MAX_BACKOFF_MS);
        console.warn(`[AI Service] Request failed on attempt ${attempt + 1}/${maxRetries + 1}, retrying in ${backoffMs}ms:`, error.message);
        await delay(backoffMs);
        continue;
      }
      
      // Final attempt failed, throw the error
      throw error;
    }
  }
}

/**
 * Fetch stored AI analysis from Airtable via backend with retry logic.
 * @param {string} profileUrl
 * @param {Object} [options] - optional { signal, retries }
 * @returns {Promise<{success: boolean, exists: boolean, analysis?: Object, meta?: Object}>}
 */
export async function getStoredAnalysis(profileUrl, options = {}) {
  const { signal, retries = AI_SERVICE_CONFIG.STORED_ANALYSIS_RETRIES } = options;
  const maxRetries = Math.max(0, retries);
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Create timeout controller (10 seconds for simple GET request)
      const timeoutController = new AbortController();
      const timeoutId = setTimeout(() => timeoutController.abort(), 10000);
      
      const combinedSignal = signal || timeoutController.signal;
      
      const res = await fetch(`${BASE_URL}/api/ai/get-analysis?url=${encodeURIComponent(profileUrl)}`, { signal: combinedSignal });
      
      clearTimeout(timeoutId);
      const text = await res.text();
      let data = null;
      
      try {
        data = text ? JSON.parse(text) : null;
      } catch (e) {
        console.warn(`[AI Service] JSON parse error on stored analysis attempt ${attempt + 1}:`, e.message);
        throw new Error(`Stored analysis response is not valid JSON: ${text?.slice(0, 300)}`);
      }
      
      if (!res.ok) {
        // Retry on 5xx errors
        if (res.status >= 500 && attempt < maxRetries) {
          const backoffMs = Math.min(1000 * Math.pow(2, attempt), AI_SERVICE_CONFIG.MAX_BACKOFF_MS);
          console.warn(`[AI Service] Server error on stored analysis attempt ${attempt + 1}/${maxRetries + 1}, retrying in ${backoffMs}ms`);
          await delay(backoffMs);
          continue;
        }
        
        const err = (data && (data.error || data.details)) || res.statusText || 'Get analysis failed';
        throw new Error(err);
      }
      
      return data;
      
    } catch (error) {
      if (error.name === 'AbortError') {
        throw error; // Don't retry aborted requests
      }
      
      if (attempt < maxRetries) {
        const backoffMs = Math.min(1000 * Math.pow(2, attempt), AI_SERVICE_CONFIG.MAX_BACKOFF_MS);
        console.warn(`[AI Service] Stored analysis request failed on attempt ${attempt + 1}/${maxRetries + 1}, retrying in ${backoffMs}ms:`, error.message);
        await delay(backoffMs);
        continue;
      }
      
      throw error;
    }
  }
}
